import React from 'react';
import { Menu, theme, type MenuProps } from 'antd';
import {
  DashboardOutlined,
  ShoppingCartOutlined,
  PercentageOutlined,
  DesktopOutlined, // POS-terminal
  UserOutlined,
  SettingOutlined,
  TeamOutlined,
  BarChartOutlined,
  FileTextOutlined,
  BankOutlined,
  SwapOutlined,
  InboxOutlined,
  TruckOutlined,
  DatabaseOutlined,
  AuditOutlined,
  CalendarOutlined,
  ScanOutlined,
  SafetyOutlined,
  LineChartOutlined,
  ReconciliationOutlined,
  CreditCardOutlined,
  ShopOutlined,
  FileDoneOutlined,
} from '@ant-design/icons';
import { Link, useLocation } from 'react-router-dom';
import { StyledSider, Logo } from '../../styles/common';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { hasPermission, PERMISSIONS } from '../../utils/permissions';
import styled from 'styled-components';

const StyledMenu = styled(Menu)`
  &.ant-menu {
    /* Style for the selected menu item (including submenu items) */
    .ant-menu-item-selected {
      font-weight: bold;
      background-color: ${(props) => (props.theme.isDarkMode ? '#333333' : '#f0f0f0')} !important;
      color: ${(props) => (props.theme.isDarkMode ? '#ffffff' : '#000000')} !important;

      .anticon {
        color: ${(props) => (props.theme.isDarkMode ? '#ffffff' : '#000000')} !important;
      }
    }

    /* Ensure the parent submenu title is bold */
    .ant-menu-submenu-title {
      font-weight: bold !important;
    }

    /* Ensure the main menu item (submenu parent) is white when any submenu is selected */
    .ant-menu-submenu-selected > .ant-menu-submenu-title {
      color: ${(props) => (props.theme.isDarkMode ? '#ffffff' : '#000000')} !important;

      .anticon {
        color: ${(props) => (props.theme.isDarkMode ? '#ffffff' : '#000000')} !important;
      }
    }

    /* Prevent the entire submenu from getting a background */
    .ant-menu-submenu-selected {
      background-color: transparent !important;
    }

    /* Hover effect for menu items */
    .ant-menu-item:hover {
      background-color: ${(props) => (props.theme.isDarkMode ? '#444444' : '#e0e0e0')} !important;
      color: ${(props) => (props.theme.isDarkMode ? '#ffffff' : '#000000')} !important;
    }

    /* Make sidebar menu links fill the menu item area */
    .sidebar-menu-link {
      display: block;
      width: 100%;
      color: inherit;
      text-decoration: none;
    }
  }
`;


// Delivery Module Menu Items
const deliveryMenuItems = [
  {
    key: 'delivery',
    icon: <DashboardOutlined />,
    label: 'Delivery Dashboard',
    path: '/delivery',
  },
  {
    key: 'tog',
    icon: <InboxOutlined />,
    label: 'TOG',
    children: [
      { key: 'tog-received', label: 'TOG Received', path: '/tog-received' },
      { key: 'tog-requests', label: 'TOG Requests', path: '/tog-requests' },
    ],
  },
  {
    key: 'packing',
    icon: <InboxOutlined />,
    label: 'Packing',
    children: [
      { key: 'tog-packing', label: 'TOG Packing', path: '/tog-packing' },
      { key: 'tog-unpacking', label: 'TOG Unpacking', path: '/tog-unpacking' },
    ],
  },
  {
    key: 'shipping',
    icon: <TruckOutlined />,
    label: 'Shipping',
    children: [
      { key: 'shipment-batch', label: 'Outbound', path: '/shipment-batch' },
      { key: 'loading', label: 'Loading', path: '/loading' },
      { key: 'inbound', label: 'Inbound', path: '/inbound' },
    ],
  },
  {
    key: 'tog-reports',
    icon: <BarChartOutlined />,
    label: 'TOG Reports',
    children: [
      { key: 'tog-reports/sent',          label: 'TOG Sent Report',     path: '/tog-reports/sent' },
      { key: 'tog-reports/received',       label: 'TOG Received Report', path: '/tog-reports/received' },
      { key: 'tog-reports/staff-summary',  label: 'Staff Summary',       path: '/tog-reports/staff-summary' },
    ],
  },
];

// Cycle Counting Module Menu Items
const cycleCountMenuItems = [
  {
    key: 'cycle-counts',
    icon: <AuditOutlined />,
    label: 'Cycle Counts',
    path: '/cycle-counting/cycle-counts',
  },
  {
    key: 'cycle-periods',
    icon: <CalendarOutlined />,
    label: 'Count Periods',
    path: '/cycle-counting/periods',
  },
  {
    key: 'stock-taking',
    icon: <ScanOutlined />,
    label: 'Stock Taking',
    path: '/cycle-counting/stock-taking',
  },
  {
    key: 'cycle-reports',
    icon: <LineChartOutlined />,
    label: 'Reports',
    path: '/cycle-counting/reports',
  },
];

// Credit Payments Module Menu Items
const creditPaymentMenuItems = [
  {
    key: 'credit-payments',
    icon: <CreditCardOutlined />,
    label: 'Outstanding',
    path: '/credit-payments',
  },
];

// Access Control Module Menu Items
const accessControlMenuItems = [
  {
    key: 'access-control-dashboard',
    icon: <DatabaseOutlined />,
    label: 'Dashboard',
    path: '/access-control',
  },
  {
    key: 'access-control-users',
    icon: <UserOutlined />,
    label: 'User Management',
    path: '/access-control/users',
  },
  {
    key: 'access-control-roles',
    icon: <TeamOutlined />,
    label: 'Role Management',
    path: '/access-control/roles',
  },
  {
    key: 'access-control-permissions',
    icon: <SafetyOutlined />,
    label: 'Permission Management',
    path: '/access-control/permissions',
  },
  {
    key: 'access-control-settings',
    icon: <SettingOutlined />,
    label: 'System Settings',
    path: '/access-control/settings',
  },
];

// E-Commerce Module Menu Items
const ecommerceMenuItems = [
  { key: 'ecommerce', icon: <DashboardOutlined />, label: 'Dashboard', path: '/ecommerce' },
  { key: 'ecommerce/orders', icon: <ShoppingCartOutlined />, label: 'Orders', path: '/ecommerce/orders' },
  { key: 'ecommerce/payment-discounts', icon: <PercentageOutlined />, label: 'Payment Discounts', path: '/ecommerce/payment-discounts' },
  { key: 'ecommerce/click-collect', icon: <ShopOutlined />, label: 'Click & Collect', path: '/ecommerce/click-collect' },
  { key: 'ecommerce/bills', icon: <FileTextOutlined />, label: 'Bills', path: '/ecommerce/bills' },
  { key: 'ecommerce/customers', icon: <UserOutlined />, label: 'Web Customers', path: '/ecommerce/customers' },
  { key: 'ecommerce/reports', icon: <BarChartOutlined />, label: 'Reports', path: '/ecommerce/reports' },
  { key: 'ecommerce/cms/banners', icon: <FileDoneOutlined />, label: 'CMS', path: '/ecommerce/cms/banners' },
];

// Purchases Module Menu Items
const purchasesMenuItems = [
  {
    key: 'purchases',
    icon: <ShopOutlined />,
    label: 'Purchases',
    children: [
      { key: 'purchase-orders', label: 'Purchase Orders', path: '/purchases/orders' },
      { key: 'supplier-invoices', label: 'Supplier Invoices', path: '/purchases/invoices' },
      { key: 'grn', label: 'GRN', path: '/purchases/grns' },
    ],
  },
];

// Sales Module Menu Items
// Note: permission gating for these keys is looked up by key in
// getPermissionForMenuItem()'s map below, not via an inline field here —
// matching every other module's convention in this file.
const salesMenuItems = [
  {
    key: 'sales',
    icon: <ShoppingCartOutlined />,
    label: 'Sales',
    children: [
      { key: 'sales-orders', label: 'Sales Orders', path: '/sales/orders' },
      { key: 'sales-quotations', label: 'Direct Quotations', path: '/sales/quotations' },
      { key: 'sales-returns', label: 'Sales Returns', path: '/sales/returns' },
      { key: 'sales-reports', label: 'Sales Report', path: '/sales/reports' },
    ],
  },
];

// POS Terminal Module Menu Items (POS-terminal, Plan B)
const posMenuItems = [
  {
    key: 'pos-admin',
    icon: <DesktopOutlined />,
    label: 'POS Terminal',
    children: [
      { key: 'pos-till', label: 'Open till', path: '/pos/till' },
      { key: 'pos-terminals', label: 'Terminals', path: '/pos/admin/terminals' },
      { key: 'pos-payment-modes', label: 'Payment Modes', path: '/pos/admin/payment-modes' },
      { key: 'pos-discount-tiers', label: 'Discount Tiers', path: '/pos/admin/discount-tiers' },
    ],
  },
];

// Inventory Module Menu Items
const inventoryMenuItems = [
  {
    key: 'inventory',
    icon: <DatabaseOutlined />,
    label: 'Inventory',
    children: [
      { key: 'inventory/items', label: 'Items', path: '/inventory/items' },
      { key: 'inventory/bulk-isbn-fetch', label: 'Bulk ISBN Fetch', path: '/inventory/bulk-isbn-fetch' },
    ],
  },
  {
    key: 'inventory-master-records',
    icon: <DatabaseOutlined />,
    label: 'Master Records',
    children: [
      { key: 'inventory/departments', label: 'Departments', path: '/inventory/departments' },
      { key: 'inventory/categories', label: 'Categories', path: '/inventory/categories' },
      { key: 'inventory/publishers', label: 'Publishers', path: '/inventory/publishers' },
      { key: 'inventory/authors', label: 'Authors', path: '/inventory/authors' },
      { key: 'inventory/racks', label: 'Racks', path: '/inventory/racks' },
      { key: 'inventory/tags', label: 'Tags', path: '/inventory/tags' },
      { key: 'inventory/groups', label: 'Groups', path: '/inventory/groups' },
      { key: 'inventory/currencies', label: 'Currencies', path: '/inventory/currencies' },
      { key: 'inventory/languages', label: 'Languages', path: '/inventory/languages' },
      { key: 'inventory/regions', label: 'Regions', path: '/inventory/regions' },
    ],
  },
];

// Warehouse Module Menu Items
const warehouseMenuItems = [
  {
    key: 'loyalty',
    icon: <BarChartOutlined />,
    label: 'Loyalty',
    children: [
      { key: 'loyalty-overview', label: 'Overview', path: '/warehouse/loyalty-overview' },
      { key: 'loyalty-customers', label: 'Loyalty Customers', path: '/warehouse/loyalty-customers' },
    ],
  },
  {
    key: 'customer-sales',
    icon: <TeamOutlined />,
    label: 'Customer Sales',
    children: [
      { key: 'web-customers', label: 'Web Customers', path: '/warehouse/customer-sales/web' },
      { key: 'pos-customers', label: 'POS Customers', path: '/warehouse/customer-sales/pos' },
    ],
  },
  {
    key: 'fast-moving',
    icon: <BarChartOutlined />,
    label: 'Item Movement',
    children: [
      { key: 'warehouse/fast-moving', label: 'Fast Moving Items', path: '/warehouse/fast-moving' },
      { key: 'warehouse/fast-moving/departments', label: 'Dept / Category Movement', path: '/warehouse/fast-moving/departments' },
    ],
  },
  {
    key: 'reorder',
    icon: <LineChartOutlined />,
    label: 'Reorder Management',
    children: [
      { key: 'warehouse/reorder', label: 'Reorder Report', path: '/warehouse/reorder' },
      { key: 'warehouse/reorder/item-reorder-list', label: 'Item Reorder List', path: '/warehouse/reorder/item-reorder-list' },
      { key: 'warehouse/reorder/arrivals', label: 'Arrivals Report', path: '/warehouse/reorder/arrivals' },
      { key: 'warehouse/reorder/settings', label: 'Settings', path: '/warehouse/reorder/settings' },
    ],
  },
  {
    key: 'sales-reports',
    icon: <BarChartOutlined />,
    label: 'Sales Reports',
    children: [
      { key: 'warehouse/sales-reports/overall', label: 'Overall Sales Report', path: '/warehouse/sales-reports/overall' },
      { key: 'warehouse/sales-reports/publisher', label: 'Publisher Sales Report', path: '/warehouse/sales-reports/publisher' },
    ],
  },
  {
    key: 'mis-reports',
    icon: <BarChartOutlined />,
    label: 'MIS Reports',
    children: [
      { key: 'warehouse/mis-reports', label: 'Overview Dashboard', path: '/warehouse/mis-reports' },
      { key: 'warehouse/mis-reports/seasonal', label: 'Seasonal Patterns', path: '/warehouse/mis-reports/seasonal' },
      { key: 'warehouse/mis-reports/sales-trends', label: 'Sales Trends', path: '/warehouse/mis-reports/sales-trends' },
      { key: 'warehouse/mis-reports/demand-forecast', label: 'Demand Forecast', path: '/warehouse/mis-reports/demand-forecast' },
      { key: 'warehouse/mis-reports/planning', label: 'Planning Summary', path: '/warehouse/mis-reports/planning' },
    ],
  },
];

interface SidebarProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
  isMobile?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onCollapse, isMobile = false }) => {
  const location = useLocation();
  const { isDarkMode } = useTheme();
  const { permissions } = useAuth();
  const { token } = theme.useToken();

  const getSelectedKeys = () => {
    const pathname = location.pathname;

    if (pathname.startsWith('/ecommerce')) {
      const hit = ecommerceMenuItems.slice(1).find((i) => pathname.startsWith('/' + i.key.replace('/banners', '')));
      return [hit ? hit.key : 'ecommerce'];
    }

    // Match access-control menu items by full path prefix
    if (pathname.startsWith('/access-control')) {
      if (pathname.startsWith('/access-control/users')) return ['access-control-users'];
      if (pathname.startsWith('/access-control/roles')) return ['access-control-roles'];
      if (pathname.startsWith('/access-control/permissions')) return ['access-control-permissions'];
      if (pathname.startsWith('/access-control/settings')) return ['access-control-settings'];
      return ['access-control-dashboard'];
    }

    const path = pathname.substring(1);
    const mainPath = path.split('/')[0];
    if (!path) return ['dashboard'];
    const isSubmenuItem = path.includes('/');
    if (isSubmenuItem) {
      return [path, mainPath];
    }
    return [mainPath];
  };

  // Check if current route is access-control-related
  const isAccessControlRoute = location.pathname.startsWith('/access-control');

  // Check if current route is delivery-related
  const isDeliveryRoute = location.pathname.startsWith('/delivery') || 
                          location.pathname.startsWith('/tog') || 
                          location.pathname.startsWith('/shipment-batch') || 
                          location.pathname.startsWith('/inbound') ||
                          location.pathname.startsWith('/loading') ||
                          location.pathname.startsWith('/tog-reports');

  // Check if current route is warehouse-related
  const isWarehouseRoute = location.pathname.startsWith('/warehouse');

  // Check if current route is cycle-counting-related
  const isCycleCountRoute = location.pathname.startsWith('/cycle-counting');

  // Check if current route is credit-payments-related
  const isCreditPaymentRoute = location.pathname.startsWith('/credit-payments');

  // Check if current route is purchases-related
  const isPurchasesRoute = location.pathname.startsWith('/purchases');

  // Check if current route is inventory-related
  const isInventoryRoute = location.pathname.startsWith('/inventory');

  // Check if current route is sales-related
  const isSalesRoute = location.pathname.startsWith('/sales');

  const isEcommerceRoute = location.pathname.startsWith('/ecommerce');
  // Check if current route is POS-terminal-related (POS-terminal, Plan B)
  const isPosRoute = location.pathname.startsWith('/pos');

  const renderMenuItems = (items: typeof deliveryMenuItems | typeof warehouseMenuItems): MenuProps['items'] => {
    return items.map((item: any) => {
      // Check permission for the main menu item
      const itemPermission = getPermissionForMenuItem(item.key);
      if (itemPermission && !hasPermission(itemPermission, 'view', permissions || undefined)) {
        return null;
      }

      if (item.children) {
        // Filter children based on permissions
        const filteredChildren = item.children.filter((child: any) => {
          const childPermission = getPermissionForMenuItem(child.key);
          return !childPermission || hasPermission(childPermission, 'view', permissions || undefined);
        });

        if (filteredChildren.length === 0) {
          return null;
        }

        return {
          key: item.key,
          icon: item.icon,
          label: item.label,
          children: filteredChildren.map((child: any) => ({
            key: child.key,
            label: (
              <Link to={child.path} className="sidebar-menu-link">
                {child.label}
              </Link>
            ),
          })),
        };
      }

      return {
        key: item.key,
        icon: item.icon,
        label: (
          <Link to={item.path} className="sidebar-menu-link">
            {item.label}
          </Link>
        ),
      };
    }).filter(Boolean) as MenuProps['items'];
  };

  const getPermissionForMenuItem = (key: string): string | null => {
    const permissionMap: Record<string, string> = {
      // Access Control
      'access-control-dashboard': PERMISSIONS.ACCESS_CONTROL,
      'access-control-users': PERMISSIONS.ACCESS_CONTROL_USERS,
      'access-control-roles': PERMISSIONS.ACCESS_CONTROL_ROLES,
      'access-control-permissions': PERMISSIONS.ACCESS_CONTROL_PERMISSIONS,
      'access-control-settings': PERMISSIONS.ACCESS_CONTROL_SYSTEM_SETTINGS,
      
      // Delivery Management
      'delivery': PERMISSIONS.DELIVERY_DASHBOARD,
      'tog-received': PERMISSIONS.DELIVERY_TOG_RECEIVED,
      'tog-requests': PERMISSIONS.DELIVERY_TOG_REQUESTS,
      'tog-packing': PERMISSIONS.DELIVERY_PACKING_TOG_PACKING,
      'tog-unpacking': PERMISSIONS.DELIVERY_PACKING_TOG_UNPACKING,
      'shipment-batch': PERMISSIONS.DELIVERY_SHIPMENT_OUTBOUND,
      'loading': PERMISSIONS.DELIVERY_SHIPMENT_LOADING,
      'inbound': PERMISSIONS.DELIVERY_SHIPMENT_INBOUND,
      'tog-reports/sent':         PERMISSIONS.DELIVERY_TOG_REPORTS,
      'tog-reports/received':     PERMISSIONS.DELIVERY_TOG_REPORTS,
      'tog-reports/staff-summary': PERMISSIONS.DELIVERY_TOG_REPORTS,
      
      // Warehouse Management
      'loyalty-overview': PERMISSIONS.WAREHOUSE_LOYALTY_OVERVIEW,
      'loyalty-customers': PERMISSIONS.WAREHOUSE_LOYALTY_CUSTOMERS,
      'web-customers': PERMISSIONS.WAREHOUSE_SALES_WEB,
      'pos-customers': PERMISSIONS.WAREHOUSE_SALES_POS,
      'warehouse/reorder': PERMISSIONS.WAREHOUSE_REORDER_REPORT,
      'warehouse/reorder/item-reorder-list': PERMISSIONS.WAREHOUSE_REORDER_REPORT,
      'warehouse/reorder/arrivals': PERMISSIONS.WAREHOUSE_REORDER_ARRIVALS,
      'warehouse/reorder/settings': PERMISSIONS.WAREHOUSE_REORDER_SETTINGS,
      'warehouse/fast-moving': PERMISSIONS.WAREHOUSE_FAST_MOVING,
      'warehouse/fast-moving/departments': PERMISSIONS.WAREHOUSE_FAST_MOVING,
      'warehouse/sales-reports/overall': PERMISSIONS.WAREHOUSE_SALES_REPORT_OVERALL,
      'warehouse/sales-reports/publisher': PERMISSIONS.WAREHOUSE_SALES_REPORT_PUBLISHER,
      'warehouse/mis-reports': PERMISSIONS.WAREHOUSE_MIS_REPORTS,
      'warehouse/mis-reports/seasonal': PERMISSIONS.WAREHOUSE_MIS_REPORTS,
      'warehouse/mis-reports/sales-trends': PERMISSIONS.WAREHOUSE_MIS_REPORTS,
      'warehouse/mis-reports/demand-forecast': PERMISSIONS.WAREHOUSE_MIS_REPORTS,
      'warehouse/mis-reports/planning': PERMISSIONS.WAREHOUSE_MIS_REPORTS,
      
      // Cycle Counting
      'cycle-counting/cycle-counts': PERMISSIONS.CYCLE_COUNTS,
      'cycle-counting/periods': PERMISSIONS.CYCLE_PERIODS,
      'cycle-counting/stock-taking': PERMISSIONS.CYCLE_STOCK_TAKING,
      'cycle-counting/reports': PERMISSIONS.CYCLE_REPORTS,
      
      // Credit Management
      'credit-payments': PERMISSIONS.CREDIT_PAYMENTS_VIEW,
      
      // Purchase Management
      'purchases/orders': PERMISSIONS.PURCHASE_MANAGEMENT,
      'purchases/invoices': PERMISSIONS.PURCHASE_MANAGEMENT,
      'purchases/grns': PERMISSIONS.PURCHASE_MANAGEMENT,

      // Sales Management
      'sales-orders': PERMISSIONS.SALES_ORDERS,
      'sales-quotations': PERMISSIONS.SALES_QUOTATIONS,
      'sales-returns': PERMISSIONS.SALES_ORDERS,
      'sales-reports': PERMISSIONS.SALES_ORDERS,
      // POS-terminal: the pages also check the real role via /pos/me
      'pos-till': PERMISSIONS.POS_SELL,
      'pos-terminals': PERMISSIONS.POS_MANAGE,
      'pos-payment-modes': PERMISSIONS.POS_MANAGE,
      'pos-discount-tiers': PERMISSIONS.POS_MANAGE,

      // Inventory Management
      'inventory/items': PERMISSIONS.INVENTORY_ITEMS,
      'inventory/departments': PERMISSIONS.INVENTORY_MASTER_DATA,
      'inventory/categories': PERMISSIONS.INVENTORY_MASTER_DATA,
      'inventory/publishers': PERMISSIONS.INVENTORY_PUBLISHER,
      'inventory/authors': PERMISSIONS.INVENTORY_AUTHOR,
      'inventory/racks': PERMISSIONS.INVENTORY_RACK,
      'inventory/tags': PERMISSIONS.INVENTORY_TAG,
      'inventory/groups': PERMISSIONS.INVENTORY_GROUP,
      'inventory/currencies': PERMISSIONS.INVENTORY_CURRENCY,
      'inventory/languages': PERMISSIONS.INVENTORY_LANGUAGE,
      'inventory/regions': PERMISSIONS.INVENTORY_REGION,
      'inventory/bulk-isbn-fetch': PERMISSIONS.INVENTORY_BULK_FETCH,
    };
    
    return permissionMap[key] || null;
  };

  // Don't show sidebar for control panel or non-module routes
  if (!isAccessControlRoute && !isDeliveryRoute && !isWarehouseRoute && !isCycleCountRoute && !isCreditPaymentRoute && !isPurchasesRoute && !isInventoryRoute && !isSalesRoute && !isEcommerceRoute && !isPosRoute) {
    return null;
  }

  // Determine which menu items to show
  const menuItems = isEcommerceRoute
    ? ecommerceMenuItems
    : isAccessControlRoute
    ? accessControlMenuItems
    : isWarehouseRoute
    ? warehouseMenuItems
    : isCycleCountRoute
    ? cycleCountMenuItems
    : isCreditPaymentRoute
    ? creditPaymentMenuItems
    : isPurchasesRoute
    ? purchasesMenuItems
    : isInventoryRoute
    ? inventoryMenuItems
    : isSalesRoute
    ? salesMenuItems
    : isPosRoute
    ? posMenuItems
    : deliveryMenuItems;
  // On mobile, sidebar always shows full width when visible (collapsed = hidden)
  const showFullText = isMobile ? true : !collapsed;
  const logoText = isEcommerceRoute
    ? (showFullText ? 'E-Commerce' : 'EC')
    : isAccessControlRoute
    ? (showFullText ? 'Access Control' : 'AC')
    : isWarehouseRoute
    ? (showFullText ? 'Warehouse' : 'WH')
    : isCycleCountRoute
    ? (showFullText ? 'Cycle Count' : 'CC')
    : isCreditPaymentRoute
    ? (showFullText ? 'Credit Payments' : 'CP')
    : isPurchasesRoute
    ? (showFullText ? 'Purchases' : 'PUR')
    : isInventoryRoute
    ? (showFullText ? 'Inventory' : 'INV')
    : isSalesRoute
    ? (showFullText ? 'Sales' : 'SAL')
    : isPosRoute
    ? (showFullText ? 'POS Terminal' : 'POS')
    : (showFullText ? 'Delivery System' : 'DMS');

  return (
    <StyledSider
      collapsible
      collapsed={isMobile ? false : collapsed}
      onCollapse={onCollapse}
      width={isMobile ? 250 : 250}
      collapsedWidth={isMobile ? 0 : 80}
      trigger={null}
      style={isMobile ? {
        position: 'fixed',
        height: '100vh',
        left: 0,
        top: 0,
        zIndex: 1001,
        transform: collapsed ? 'translateX(-100%)' : 'translateX(0)',
        transition: 'transform 0.3s ease',
      } : undefined}
    >
      <Logo>{logoText}</Logo>
      <StyledMenu
        mode="inline"
        defaultSelectedKeys={isEcommerceRoute ? ['ecommerce'] : isPosRoute ? ['pos-terminals'] : isAccessControlRoute ? ['access-control-dashboard'] : isWarehouseRoute ? ['loyalty-customers'] : isCycleCountRoute ? ['cycle-counts'] : isCreditPaymentRoute ? ['credit-payments'] : isPurchasesRoute ? ['purchase-orders'] : isInventoryRoute ? ['inventory/items'] : isSalesRoute ? ['sales-orders'] : ['delivery']}
        selectedKeys={getSelectedKeys()}
        defaultOpenKeys={isAccessControlRoute ? [] : isWarehouseRoute ? (location.pathname.startsWith('/warehouse/reorder') ? ['reorder'] : location.pathname.startsWith('/warehouse/customer-sales') ? ['customer-sales'] : location.pathname.startsWith('/warehouse/fast-moving') ? ['fast-moving'] : location.pathname.startsWith('/warehouse/mis-reports') ? ['mis-reports'] : ['loyalty']) : isCycleCountRoute ? [] : isCreditPaymentRoute ? [] : isPurchasesRoute ? ['purchases'] : isInventoryRoute ? (location.pathname.startsWith('/inventory/items') || location.pathname.startsWith('/inventory/bulk-isbn-fetch') ? ['inventory'] : ['inventory-master-records']) : isSalesRoute ? ['sales'] : isPosRoute ? ['pos-admin'] : [location.pathname.split('/')[1]]}
        style={{ borderRight: 0, flex: 1 }}
        items={renderMenuItems(menuItems)}
      />
    </StyledSider>
  );
};

export default Sidebar;
